import { Component, JsonFile } from "projen";
import { TextFile } from "projen/lib/textfile";
import { CursorSettingsConfig } from "../agent-config-options";
import {
  AGENT_RULE_SCOPE,
  AgentRule,
  AgentSkill,
  AgentSubAgent,
  McpServerConfig,
} from "../types";

const GENERATED_MARKER =
  "# ~~ Generated by @codedrifters/configulator. Edits welcome — please contribute improvements back. ~~";

/**
 * Renders agent configuration files for Cursor.
 *
 * Output files:
 * - `.cursor/rules/{name}.mdc` — YAML frontmatter + markdown body
 * - `.cursor/agents/{name}.md` — sub-agent definitions
 * - .cursor/mcp.json — MCP server configuration
 * - .cursor/hooks.json — lifecycle hooks
 * - .cursorignore — hard security boundary
 * - .cursorindexingignore — soft indexing boundary
 */
export class CursorRenderer {
  /**
   * Render all Cursor configuration files.
   */
  public static render(
    component: Component,
    rules: ReadonlyArray<AgentRule>,
    skills: ReadonlyArray<AgentSkill>,
    subAgents: ReadonlyArray<AgentSubAgent>,
    mcpServers: Readonly<Record<string, McpServerConfig>>,
    settings?: CursorSettingsConfig,
  ): void {
    CursorRenderer.renderRules(component, rules);
    CursorRenderer.renderSkills(component, skills);
    CursorRenderer.renderSubAgents(component, subAgents);
    CursorRenderer.renderMcpServers(component, mcpServers);
    CursorRenderer.renderHooks(component, settings);
    CursorRenderer.renderIgnoreFiles(component, settings);
  }

  private static renderRules(
    component: Component,
    rules: ReadonlyArray<AgentRule>,
  ): void {
    for (const rule of rules) {
      if (rule.platforms?.cursor?.exclude) continue;

      const lines: string[] = [];
      const description =
        rule.platforms?.cursor?.description ?? rule.description;
      const isAlways = rule.scope === AGENT_RULE_SCOPE.ALWAYS;

      // YAML frontmatter
      lines.push("---");
      lines.push(`description: "${description}"`);
      lines.push(`alwaysApply: ${isAlways}`);
      if (!isAlways && rule.filePatterns && rule.filePatterns.length > 0) {
        lines.push(`path: ${JSON.stringify([...rule.filePatterns])}`);
      }
      lines.push("---");
      lines.push("");
      lines.push(...rule.content.split("\n"));

      new TextFile(component, `.cursor/rules/${rule.name}.mdc`, { lines });
    }
  }

  private static renderSkills(
    component: Component,
    skills: ReadonlyArray<AgentSkill>,
  ): void {
    for (const skill of skills) {
      if (skill.platforms?.cursor?.exclude) continue;

      const lines: string[] = [];
      lines.push("---");
      lines.push(`name: "${skill.name}"`);
      lines.push(`description: "${skill.description}"`);
      if (skill.disableModelInvocation) {
        lines.push(`disable-model-invocation: true`);
      }
      if (skill.userInvocable === false) {
        lines.push(`user-invocable: false`);
      }
      if (skill.context) {
        lines.push(`context: "${skill.context}"`);
      }
      if (skill.agent) {
        lines.push(`agent: "${skill.agent}"`);
      }
      if (skill.shell) {
        lines.push(`shell: "${skill.shell}"`);
      }
      if (skill.allowedTools && skill.allowedTools.length > 0) {
        lines.push(`allowed-tools:`);
        for (const tool of skill.allowedTools) {
          lines.push(`  - "${tool}"`);
        }
      }
      lines.push("---");
      lines.push("");
      lines.push(...skill.instructions.split("\n"));

      new TextFile(component, `.cursor/skills/${skill.name}/SKILL.md`, {
        lines,
      });

      if (skill.referenceFiles && skill.referenceFiles.length > 0) {
        for (const file of skill.referenceFiles) {
          new TextFile(component, `.cursor/skills/${skill.name}/${file.path}`, {
            lines: file.content.split("\n"),
          });
        }
      }
    }
  }

  private static renderSubAgents(
    component: Component,
    subAgents: ReadonlyArray<AgentSubAgent>,
  ): void {
    for (const agent of subAgents) {
      if (agent.platforms?.cursor?.exclude) continue;

      const lines: string[] = [];
      lines.push("---");
      lines.push(`name: ${agent.name}`);
      lines.push(`description: >-`);
      lines.push(`  ${agent.description}`);
      // Cursor uses its own model selection; omit the model field
      // to let it use its default (e.g., composer).
      if (agent.platforms?.cursor?.readonly) {
        lines.push(`readonly: true`);
      }
      if (agent.platforms?.cursor?.isBackground) {
        lines.push(`is_background: true`);
      }
      lines.push("---");
      lines.push("");
      lines.push(...agent.prompt.split("\n"));

      new TextFile(component, `.cursor/agents/${agent.name}.md`, { lines });
    }
  }

  private static renderMcpServers(
    component: Component,
    mcpServers: Readonly<Record<string, McpServerConfig>>,
  ): void {
    const serverNames = Object.keys(mcpServers);
    if (serverNames.length === 0) return;

    const obj: Record<string, unknown> = { mcpServers: {} };
    const servers = obj.mcpServers as Record<string, unknown>;

    for (const [name, config] of Object.entries(mcpServers)) {
      const server: Record<string, unknown> = {};
      if (config.transport) server.transport = config.transport;
      if (config.command) server.command = config.command;
      if (config.args) server.args = [...config.args];
      if (config.url) server.url = config.url;
      if (config.headers && Object.keys(config.headers).length > 0) {
        server.headers = { ...config.headers };
      }
      if (config.env) server.env = { ...config.env };
      servers[name] = server;
    }

    new JsonFile(component, ".cursor/mcp.json", { obj });
  }

  private static renderHooks(
    component: Component,
    settings?: CursorSettingsConfig,
  ): void {
    if (!settings?.hooks) return;

    const hooks: Record<string, unknown[]> = {};
    const hookEntries = settings.hooks;

    // Render all hook events dynamically
    for (const [event, actions] of Object.entries(hookEntries)) {
      if (actions && actions.length > 0) {
        hooks[event] = actions.map((h: { command: string }) => ({
          command: h.command,
        }));
      }
    }

    if (Object.keys(hooks).length === 0) return;

    new JsonFile(component, ".cursor/hooks.json", {
      obj: { version: 1, hooks },
    });
  }

  private static renderIgnoreFiles(
    component: Component,
    settings?: CursorSettingsConfig,
  ): void {
    if (settings?.ignorePatterns && settings.ignorePatterns.length > 0) {
      new TextFile(component, ".cursorignore", {
        lines: [GENERATED_MARKER, "", ...settings.ignorePatterns],
      });
    }

    if (
      settings?.indexingIgnorePatterns &&
      settings.indexingIgnorePatterns.length > 0
    ) {
      new TextFile(component, ".cursorindexingignore", {
        lines: [GENERATED_MARKER, "", ...settings.indexingIgnorePatterns],
      });
    }
  }
}
